import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, DollarSign, TrendingDown, Lock, Unlock, ClipboardCheck, Printer, AlertCircle, CheckCircle2, Plus, History, Eye } from 'lucide-react';
import CashRegisterTicket from '../components/CashRegisterTicket';

const API = process.env.REACT_APP_BACKEND_URL;
const DENOMS = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5];

const LIGHT = {
  green: { label: 'CUADRADO', cls: 'bg-emerald-500 text-white', text: 'text-emerald-600' },
  yellow: { label: 'DIFERENCIA MENOR', cls: 'bg-amber-400 text-amber-900', text: 'text-amber-600' },
  red: { label: 'DIFERENCIA MAYOR', cls: 'bg-rose-500 text-white', text: 'text-rose-600' },
};

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const emptyDenoms = () => DENOMS.reduce((a, d) => { a[String(d)] = ''; return a; }, {});

export default function CashRegister() {
  const { getAuthHeaders, currentBranch, user, branches } = useAuth();
  const [tab, setTab] = useState('session');
  const branchName = useMemo(() => branches.find(b => b.id === currentBranch)?.name || '', [branches, currentBranch]);

  return (
    <div data-testid="cash-register-page">
      <div className="flex items-center gap-3 mb-6">
        <Receipt className="w-6 h-6 text-[#1D3C6E]" />
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Control de Caja</h1>
        {branchName && <span className="text-sm text-slate-500">— {branchName}</span>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="session" data-testid="cr-tab-session">Sesion</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="logs" data-testid="cr-tab-logs">Historial</TabsTrigger>}
        </TabsList>
        <TabsContent value="session">
          <Session branchId={currentBranch} branchName={branchName} />
        </TabsContent>
        {user?.role === 'admin' && (
          <TabsContent value="logs">
            <Logs />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// =================== SESSION ===================
function Session({ branchId, branchName }) {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [usdRate, setUsdRate] = useState(17.5);
  const [openingFundInput, setOpeningFundInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Count form
  const [denoms, setDenoms] = useState(emptyDenoms());
  const [dollars, setDollars] = useState('');
  const [vales, setVales] = useState(['', '', '']);
  const [preview, setPreview] = useState(null);
  const [savedTicket, setSavedTicket] = useState(null);
  const [savedVariant, setSavedVariant] = useState(null);
  const [showCount, setShowCount] = useState(false);
  // Expenses
  const [showExpense, setShowExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', category: 'Otros' });
  const [expenseList, setExpenseList] = useState([]);
  const [categories, setCategories] = useState(['Otros']);

  const loadStatus = useCallback(async () => {
    if (!branchId) return;
    try {
      const r = await axios.get(`${API}/api/cash-register/status?branch_id=${branchId}`, { headers: getAuthHeaders() });
      setStatus(r.data);
      if (!openingFundInput) setOpeningFundInput(String(r.data.default_opening_fund || ''));
    } catch (err) { console.error(err); }
  }, [branchId, getAuthHeaders, openingFundInput]);

  const loadConfig = useCallback(async () => {
    try {
      const [rate, cats] = await Promise.all([
        axios.get(`${API}/api/settings/usd_mxn_rate`, { headers: getAuthHeaders() }).catch(() => ({ data: { value: 17.5 } })),
        axios.get(`${API}/api/settings/expense_categories`, { headers: getAuthHeaders() }).catch(() => ({ data: { value: ['Otros'] } })),
      ]);
      setUsdRate(Number(rate.data?.value) || 17.5);
      setCategories(cats.data?.value || ['Otros']);
    } catch { /* noop */ }
  }, [getAuthHeaders]);

  const loadExpenses = useCallback(async () => {
    if (!branchId) return;
    try {
      const r = await axios.get(`${API}/api/cash-register/expenses?branch_id=${branchId}`, { headers: getAuthHeaders() });
      setExpenseList(r.data);
    } catch { /* noop */ }
  }, [branchId, getAuthHeaders]);

  useEffect(() => { loadStatus(); loadConfig(); loadExpenses(); /* eslint-disable-next-line */ }, [branchId]);

  const openRegister = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/cash-register/open`, {
        branch_id: branchId,
        opening_fund: parseFloat(openingFundInput) || 0,
      }, { headers: getAuthHeaders() });
      await loadStatus();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const buildPayload = () => ({
    branch_id: branchId,
    denominations: Object.fromEntries(Object.entries(denoms).map(([k, v]) => [k, parseInt(v) || 0])),
    dollars: parseFloat(dollars) || 0,
    vales: vales.map(v => parseFloat(v) || 0),
    usd_rate: usdRate,
  });

  const livePreview = async () => {
    if (!branchId || !status?.is_open) return;
    try {
      const r = await axios.post(`${API}/api/cash-register/preview`, buildPayload(), { headers: getAuthHeaders() });
      setPreview(r.data);
    } catch { /* noop */ }
  };

  // Auto-preview whenever count changes
  useEffect(() => {
    if (showCount) livePreview();
    // eslint-disable-next-line
  }, [denoms, dollars, vales, showCount]);

  const submitArqueo = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/cash-register/arqueo`, buildPayload(), { headers: getAuthHeaders() });
      setSavedTicket(r.data);
      setSavedVariant('arqueo');
      setShowCount(false);
      resetForm();
      await loadStatus();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const submitClose = async () => {
    if (!window.confirm('¿Cerrar la caja? No se podran procesar mas ventas hasta una nueva apertura.')) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/cash-register/close`, buildPayload(), { headers: getAuthHeaders() });
      setSavedTicket(r.data);
      setSavedVariant('close');
      setShowCount(false);
      resetForm();
      await loadStatus();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const resetForm = () => {
    setDenoms(emptyDenoms());
    setDollars('');
    setVales(['', '', '']);
    setPreview(null);
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.post(`${API}/api/cash-register/expense`, {
        branch_id: branchId,
        amount: parseFloat(expenseForm.amount) || 0,
        description: expenseForm.description,
        category: expenseForm.category,
      }, { headers: getAuthHeaders() });
      setShowExpense(false);
      setExpenseForm({ description: '', amount: '', category: 'Otros' });
      await Promise.all([loadStatus(), loadExpenses()]);
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  if (!branchId) {
    return <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400 text-sm">Selecciona una sucursal en el panel lateral</div>;
  }

  if (!status) return <div className="text-slate-400 text-sm">Cargando...</div>;

  // ---------- CLOSED ----------
  if (!status.is_open) {
    return (
      <div className="space-y-4" data-testid="cr-closed-state">
        {savedTicket && <SavedTicketBanner data={savedTicket} variant={savedVariant} onClose={() => { setSavedTicket(null); setSavedVariant(null); }} />}
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-['Outfit']">Caja Cerrada</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Realiza la apertura para procesar ventas en {branchName || 'esta sucursal'}.</p>
          <label className="text-xs font-medium text-slate-600 block mb-1 text-left">Fondo de apertura</label>
          <Input type="number" step="0.01" value={openingFundInput} onChange={(e) => setOpeningFundInput(e.target.value)} data-testid="cr-opening-fund" />
          <p className="text-[11px] text-slate-400 mt-1 text-left">Predeterminado de la sucursal: ${(status.default_opening_fund || 0).toFixed(2)}</p>
          {error && <p className="text-sm text-rose-500 mt-3" data-testid="cr-error">{error}</p>}
          <Button onClick={openRegister} disabled={loading} className="w-full bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900 font-medium mt-4" data-testid="cr-open-register-btn">
            <Unlock className="w-4 h-4 mr-1" /> {loading ? 'Procesando...' : 'Abrir Caja'}
          </Button>
        </div>
      </div>
    );
  }

  // ---------- OPEN ----------
  const sum = status.summary || {};

  return (
    <div className="space-y-4" data-testid="cr-open-state">
      {savedTicket && <SavedTicketBanner data={savedTicket} variant={savedVariant} onClose={() => { setSavedTicket(null); setSavedVariant(null); }} />}

      {/* Header bar */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3" data-testid="cr-open-header">
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="font-semibold text-emerald-800">Caja Abierta</span>
          <span className="text-emerald-700">— por <strong>{status.opened_by}</strong> el {new Date(status.opened_at).toLocaleString('es-MX')}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setShowCount('arqueo'); resetForm(); }} data-testid="cr-arqueo-btn"><ClipboardCheck className="w-4 h-4 mr-1" /> Arqueo</Button>
          <Button size="sm" onClick={() => { setShowCount('close'); resetForm(); }} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="cr-close-btn"><Lock className="w-4 h-4 mr-1" /> Cierre</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Fondo apertura" value={fmt(status.opening_fund)} icon={DollarSign} />
        <Stat label="Efectivo (ventas)" value={fmt(sum.cash_sales)} icon={DollarSign} accent="emerald" />
        <Stat label="Tarjeta + Transf." value={fmt((sum.card_sales || 0) + (sum.transfer_sales || 0))} icon={DollarSign} />
        <Stat label="Egresos" value={fmt(sum.expenses_total)} icon={TrendingDown} accent="rose" />
        <Stat label="Esperado en caja" value={fmt(status.expected_cash)} icon={DollarSign} accent="primary" />
      </div>

      {/* Expenses */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-sm text-slate-900">Egresos de la sesion ({expenseList.length})</h3>
          <Button size="sm" variant="outline" onClick={() => setShowExpense(s => !s)} data-testid="cr-add-expense-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Registrar Egreso
          </Button>
        </div>
        {showExpense && (
          <form onSubmit={submitExpense} className="px-4 py-3 border-b border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Descripcion *</label>
              <Input value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs text-slate-500">Monto *</label>
              <Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs text-slate-500">Categoria</label>
              <select className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm" value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-4 flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowExpense(false)}>Cancelar</Button>
              <Button type="submit" size="sm" className="bg-[#1D3C6E]">Guardar</Button>
            </div>
          </form>
        )}
        {expenseList.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Sin egresos en esta sesion</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {expenseList.map(e => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">{e.description}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{e.category}</td>
                  <td className="px-4 py-2 text-xs text-slate-400">{e.user_name}</td>
                  <td className="px-4 py-2 text-right font-medium text-rose-600">-{fmt(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Count form */}
      {showCount && (
        <div className="bg-white rounded-lg border-2 border-[#1D3C6E]/20 p-5 space-y-4" data-testid="cr-count-form">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-[#1D3C6E] font-['Outfit']">
              {showCount === 'arqueo' ? 'Arqueo de Caja' : 'Cierre de Caja — Conteo Final'}
            </h3>
            <button onClick={() => { setShowCount(false); resetForm(); }} className="text-xs text-slate-400 hover:text-slate-600">Cancelar</button>
          </div>

          <CountForm denoms={denoms} setDenoms={setDenoms} dollars={dollars} setDollars={setDollars}
            usdRate={usdRate} vales={vales} setVales={setVales} />

          {/* Live comparison */}
          {preview && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Comparacion</h4>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <KV label="Esperado" value={fmt(preview.expected_cash)} />
                <KV label="Contado" value={fmt(preview.count.grand_total)} accent="primary" />
                <KV label="Diferencia" value={fmt(preview.difference)}
                  accent={preview.difference === 0 ? 'emerald' : preview.difference < 0 ? 'rose' : 'amber'} />
              </div>
              <div className={`px-3 py-2 rounded-md text-center font-bold tracking-wider text-sm ${LIGHT[preview.traffic_light].cls}`}
                data-testid="cr-traffic-light">
                {LIGHT[preview.traffic_light].label}
              </div>
              {showCount === 'close' && (
                <div className="mt-4 bg-[#1D3C6E] text-white rounded-lg p-4 text-center">
                  <p className="text-xs uppercase tracking-widest opacity-70">Efectivo a entregar</p>
                  <p className="text-3xl font-bold mt-1" data-testid="cr-deliver-cash">{fmt(preview.deliver_cash)}</p>
                  <p className="text-[11px] opacity-60 mt-1">(Total contado − fondo de apertura ${(status.default_opening_fund || 0).toFixed(2)})</p>
                </div>
              )}
            </div>
          )}

          {error && <p className="text-sm text-rose-500" data-testid="cr-count-error">{error}</p>}

          <div className="flex justify-end gap-2">
            {showCount === 'arqueo' ? (
              <Button onClick={submitArqueo} disabled={loading || !preview} className="bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900" data-testid="cr-submit-arqueo">
                <ClipboardCheck className="w-4 h-4 mr-1" /> {loading ? 'Guardando...' : 'Registrar Arqueo'}
              </Button>
            ) : (
              <Button onClick={submitClose} disabled={loading || !preview} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="cr-submit-close">
                <Lock className="w-4 h-4 mr-1" /> {loading ? 'Cerrando...' : 'Cerrar Caja'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CountForm({ denoms, setDenoms, dollars, setDollars, usdRate, vales, setVales }) {
  const updateDenom = (k, v) => setDenoms({ ...denoms, [k]: v });
  const updateVale = (i, v) => { const arr = [...vales]; arr[i] = v; setVales(arr); };
  const subtotal = (d) => (parseInt(denoms[String(d)]) || 0) * d;
  const totalDenoms = DENOMS.reduce((s, d) => s + subtotal(d), 0);
  const totalDollars = (parseFloat(dollars) || 0) * usdRate;
  const totalVales = vales.reduce((s, v) => s + (parseFloat(v) || 0), 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Denominations */}
      <div>
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Denominaciones MXN</h4>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs">
              <tr>
                <th className="px-3 py-2 text-left">Denom.</th>
                <th className="px-3 py-2 text-right w-24">Cantidad</th>
                <th className="px-3 py-2 text-right w-28">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {DENOMS.map(d => (
                <tr key={d} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-mono">${d}</td>
                  <td className="px-3 py-1.5">
                    <Input type="number" min="0" className="h-8 text-right text-sm"
                      value={denoms[String(d)]}
                      onChange={(e) => updateDenom(String(d), e.target.value)}
                      data-testid={`cr-denom-${d}`} />
                  </td>
                  <td className="px-3 py-1.5 text-right text-slate-600 font-mono">{fmt(subtotal(d))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-sm">
              <tr>
                <td className="px-3 py-2 font-semibold" colSpan={2}>Subtotal denominaciones</td>
                <td className="px-3 py-2 text-right font-bold text-[#1D3C6E]">{fmt(totalDenoms)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* USD + Vales */}
      <div className="space-y-4">
        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Dolares</h4>
          <div className="border border-slate-200 rounded-lg p-3 bg-white">
            <label className="text-xs text-slate-500">Monto USD</label>
            <Input type="number" step="0.01" min="0" value={dollars}
              onChange={(e) => setDollars(e.target.value)} data-testid="cr-dollars-input" placeholder="0.00" />
            <p className="text-xs text-slate-500 mt-2">
              ${(parseFloat(dollars) || 0).toFixed(2)} USD × ${Number(usdRate).toFixed(2)} = <span className="font-semibold text-slate-700">{fmt(totalDollars)} MXN</span>
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Vales</h4>
          <div className="border border-slate-200 rounded-lg p-3 bg-white space-y-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-14">Vale {i + 1}</span>
                <Input type="number" step="0.01" min="0" value={vales[i] || ''}
                  onChange={(e) => updateVale(i, e.target.value)}
                  className="h-8 text-sm" data-testid={`cr-vale-${i}`} placeholder="0.00" />
              </div>
            ))}
            <div className="flex justify-between text-xs pt-2 border-t border-slate-100">
              <span className="text-slate-500">Subtotal vales</span>
              <span className="font-semibold">{fmt(totalVales)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SavedTicketBanner({ data, variant, onClose }) {
  const light = LIGHT[data.traffic_light] || LIGHT.red;
  return (
    <div className="bg-white border-2 border-[#1D3C6E]/30 rounded-lg p-4 flex items-center justify-between gap-3" data-testid="cr-saved-ticket-banner">
      <div className="flex items-center gap-3">
        {variant === 'close' ? <Lock className="w-5 h-5 text-rose-500" /> : <ClipboardCheck className="w-5 h-5 text-[#AFBD20]" />}
        <div>
          <p className="text-sm font-bold text-slate-900">
            {variant === 'close' ? 'Cierre registrado' : 'Arqueo registrado'} —{' '}
            <span className={light.text}>{light.label}</span>
          </p>
          <p className="text-xs text-slate-500">
            Esperado: {fmt(data.expected_cash)} · Contado: {fmt(data.count?.grand_total)} · Dif: {fmt(data.difference)}
            {variant === 'close' && <> · Entregar: <strong>{fmt(data.deliver_cash)}</strong></>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="cr-print-saved"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
        <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-600">Cerrar</button>
      </div>
      <CashRegisterTicket data={data} variant={variant} />
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const colors = {
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    primary: 'text-[#1D3C6E]',
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <Icon className={`w-4 h-4 ${colors[accent] || 'text-slate-400'}`} />}
      </div>
      <p className={`text-xl font-bold ${colors[accent] || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function KV({ label, value, accent }) {
  const colors = {
    emerald: 'text-emerald-600',
    rose: 'text-rose-600',
    amber: 'text-amber-600',
    primary: 'text-[#1D3C6E]',
  };
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${colors[accent] || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

// =================== LOGS (Admin) ===================
function Logs() {
  const { getAuthHeaders, branches } = useAuth();
  const [list, setList] = useState([]);
  const [filterBranch, setFilterBranch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailVariant, setDetailVariant] = useState('arqueo');

  const load = async () => {
    const params = new URLSearchParams();
    if (filterBranch) params.append('branch_id', filterBranch);
    if (filterType) params.append('type', filterType);
    if (filterFrom) params.append('from', filterFrom);
    if (filterTo) params.append('to', filterTo);
    try {
      const r = await axios.get(`${API}/api/cash-register/logs?${params}`, { headers: getAuthHeaders() });
      setList(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBranch, filterType, filterFrom, filterTo]);

  const openDetail = (log) => {
    setDetail(log);
    setDetailVariant(log.type === 'close' ? 'close' : 'arqueo');
  };

  return (
    <div className="space-y-3" data-testid="cr-logs-section">
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Sucursal</label>
          <select className="h-9 px-2 border border-slate-200 rounded-md text-sm min-w-[150px]" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
            <option value="">Todas</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Tipo</label>
          <select className="h-9 px-2 border border-slate-200 rounded-md text-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Todos</option>
            <option value="open">Apertura</option>
            <option value="arqueo">Arqueo</option>
            <option value="close">Cierre</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Desde</label>
          <Input type="date" className="h-9 text-sm w-36" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Hasta</label>
          <Input type="date" className="h-9 text-sm w-36" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Fecha</th>
              <th className="text-left px-3 py-2.5">Tipo</th>
              <th className="text-left px-3 py-2.5">Sucursal</th>
              <th className="text-left px-3 py-2.5">Usuario</th>
              <th className="text-right px-3 py-2.5">Esperado</th>
              <th className="text-right px-3 py-2.5">Contado</th>
              <th className="text-right px-3 py-2.5">Dif.</th>
              <th className="text-center px-3 py-2.5">Estado</th>
              <th className="text-right px-3 py-2.5">Entregar</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-slate-400 text-xs">Sin registros</td></tr>
            ) : list.map(l => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString('es-MX')}</td>
                <td className="px-3 py-2"><TypeBadge type={l.type} /></td>
                <td className="px-3 py-2">{l.branch_name}</td>
                <td className="px-3 py-2 text-xs">{l.user_name}</td>
                <td className="px-3 py-2 text-right">{l.expected_cash != null ? fmt(l.expected_cash) : '—'}</td>
                <td className="px-3 py-2 text-right">{l.count ? fmt(l.count.grand_total) : '—'}</td>
                <td className="px-3 py-2 text-right">{l.difference != null ? fmt(l.difference) : '—'}</td>
                <td className="px-3 py-2 text-center">
                  {l.traffic_light && (
                    <span className={`status-badge ${LIGHT[l.traffic_light]?.cls || ''}`}>{LIGHT[l.traffic_light]?.label}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-medium">{l.type === 'close' ? fmt(l.deliver_cash) : '—'}</td>
                <td className="px-3 py-2 text-right">
                  {(l.type === 'arqueo' || l.type === 'close') && (
                    <button onClick={() => openDetail(l)} className="text-xs text-[#1D3C6E] hover:underline" data-testid={`cr-view-log-${l.id}`}><Eye className="w-3.5 h-3.5 inline" /> Ver</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="bg-white rounded-lg border-2 border-[#1D3C6E]/20 p-5 space-y-3" data-testid="cr-log-detail">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#1D3C6E] font-['Outfit']">
              {detail.type === 'close' ? 'Cierre' : 'Arqueo'} — {detail.branch_name} · {new Date(detail.created_at).toLocaleString('es-MX')}
            </h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
              <button onClick={() => setDetail(null)} className="text-sm text-slate-400 hover:text-slate-600">Cerrar</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV label="Fondo apertura" value={fmt(detail.opening_fund)} />
            <KV label="Esperado" value={fmt(detail.expected_cash)} />
            <KV label="Contado" value={fmt(detail.count?.grand_total)} accent="primary" />
            <KV label="Diferencia" value={fmt(detail.difference)} accent={detail.traffic_light === 'green' ? 'emerald' : detail.traffic_light === 'yellow' ? 'amber' : 'rose'} />
          </div>
          <div className={`px-3 py-2 rounded-md text-center font-bold tracking-wider text-sm ${LIGHT[detail.traffic_light]?.cls}`}>
            {LIGHT[detail.traffic_light]?.label}
          </div>
          {detail.type === 'close' && (
            <div className="bg-[#1D3C6E] text-white rounded-lg p-4 text-center">
              <p className="text-xs uppercase tracking-widest opacity-70">Efectivo entregado</p>
              <p className="text-3xl font-bold mt-1">{fmt(detail.deliver_cash)}</p>
            </div>
          )}
          <CashRegisterTicket data={detail} variant={detailVariant} />
        </div>
      )}
    </div>
  );
}

function TypeBadge({ type }) {
  const map = {
    open: { label: 'Apertura', cls: 'bg-emerald-100 text-emerald-700' },
    arqueo: { label: 'Arqueo', cls: 'bg-amber-100 text-amber-700' },
    close: { label: 'Cierre', cls: 'bg-rose-100 text-rose-700' },
  };
  const m = map[type] || { label: type, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`status-badge ${m.cls}`}>{m.label}</span>;
}
